import i18n from "@/Plugins/i18n";

import type { ChatApi } from "../Models/Api/Chat";
import type { User } from "@/Modules/Models/Domain/User";
import type {
  AllChatRoomItemType,
  ChatRoom,
  ParticipantRoomItem,
  DocumentChatRoomItem,
  CustomChatRoomItem,
  TextChatRoomItem,
} from "../Models/Domain/Chat";
import type {
  ChatRoomItemMessageWebSocket,
  DocumentChatRoomItemMessageWebSocket,
  ChatRoomParticipantWebSocket,
} from "../Models/WebSocket";
import { Role } from "@/Common/Models/Domain/Role";
import { BENDER } from "../Models/Domain/Chat";
import { CHAT_ROLE } from "../Models/WebSocket";
import { UserStatus } from "@/Common/Models/Domain/UserStatus";

import BreakTransformer from "@/Modules/Transformers/Break";
import TranscriptionTransformer, {
  type TranscriptionTransformerType,
} from "@/Modules/Transformers/User/Transcription";

import { hasValue } from "@/Utils/Objects";
import { camel } from "@/Utils/Camelize";

import {
  isTextChatRoomItemType,
  transformTextChatRoomItemMessageWebSocketToTextChatRoomItem,
} from "../MessageFilters/TextChatRoomItem";
import {
  isCustomChatRoomItem,
  transformCustomChatRoomItemMessageWebSocketToCustomChatRoomItem,
} from "../MessageFilters/CustomChatRoomItem";
import {
  isDocumentChatRoomItem,
  transformDocumentChatMessageWebSocketToDocumentChatRoomItem,
} from "../MessageFilters/DocumentChatRoomItem";
import {
  isChatUserAddedItemHandler,
  transformChatUserAddedEventMessageSocketToCustomChatRoomItem,
} from "../MessageFilters/UserAddedRoomItem";

const getChatRoomParticipantUser = (chat: ChatApi, role: Role): ChatRoomParticipantWebSocket | undefined => {
  let user: ChatRoomParticipantWebSocket | undefined = undefined;

  if (role === Role.CUSTOMER) {
    user = chat.part.find(
      (u) => (u.role === CHAT_ROLE.THERAPIST && u.left === false) || u.left === undefined
    );

    if (user === undefined) {
      user = chat.part.find((u) => (u.role === CHAT_ROLE.COACH && u.left === false) || u.left === undefined);

      if (user === undefined) {
        // Transfer pending case
        user = chat.part.find((u) => u.role === CHAT_ROLE.COACH && u.left === true && u.user !== BENDER);
      }
    }
  } else {
    user = chat.part.find((u) => (u.role === CHAT_ROLE.PATIENT && u.left === false) || u.left === undefined);
  }

  return user;
};

const transformChatRoomParticipantWebsocketToParticipantRoomItem = (
  participants: ChatRoomParticipantWebSocket[]
): ParticipantRoomItem[] => {
  return participants.filter((part) => part.user !== BENDER).map((part) => camel(part));
};

const resolveLastEvent = (
  lastEvent?: ChatRoomItemMessageWebSocket | DocumentChatRoomItemMessageWebSocket
): TextChatRoomItem | CustomChatRoomItem | DocumentChatRoomItem | undefined => {
  if (isTextChatRoomItemType(lastEvent)) {
    return transformTextChatRoomItemMessageWebSocketToTextChatRoomItem(lastEvent);
  }

  if (isCustomChatRoomItem(lastEvent)) {
    return transformCustomChatRoomItemMessageWebSocketToCustomChatRoomItem(lastEvent);
  }

  if (isDocumentChatRoomItem(lastEvent)) {
    return transformDocumentChatMessageWebSocketToDocumentChatRoomItem(lastEvent);
  }

  return undefined;
};

export const transformChatApiToChatRoom = (
  chatApi: ChatApi,
  role: Role,
  events: AllChatRoomItemType[] = [],
  sessionUser: User
): ChatRoom => {
  const user = getChatRoomParticipantUser(chatApi, role);
  const transcriptionTransformer: TranscriptionTransformerType = TranscriptionTransformer;
  const transcriptionDomain = hasValue(chatApi.transcription)
    ? transcriptionTransformer.toDomain(chatApi.transcription)
    : null;

  if (user) {
    let lastEvent = resolveLastEvent(chatApi.last_event);
    const participants = transformChatRoomParticipantWebsocketToParticipantRoomItem(chatApi.part);

    if (!hasValue(lastEvent) && isChatUserAddedItemHandler(chatApi.last_event)) {
      lastEvent = transformChatUserAddedEventMessageSocketToCustomChatRoomItem(
        chatApi.last_event,
        sessionUser,
        participants
      );
    }

    return {
      id: chatApi.chat,
      events,
      participants,
      lastEvent,
      unreadEvents: chatApi.total_unread_events,
      currentBreak: hasValue(chatApi.current_break)
        ? BreakTransformer.toDomain(chatApi.current_break)
        : undefined,
      user: {
        avatar: user.image,
        isPromo: user.is_promo,
        isB2b: user.is_b2b,
        isCouple: user.is_couple,
        isUnder18: user.is_under_18,
        status: undefined,
        chatStatus: user.state as unknown as UserStatus,
        id: user.user,
        username: user.name,
        lastConnection: chatApi.last_event?.created,
        isLoadingUser: false,
        customerUuid: user.customer_uuid,
      },
      isLoadingEvents: true,
      isAiEnabled: chatApi.is_ai_enabled,
      transcription: transcriptionDomain,
      freeSessions: chatApi.free_sessions,
    } as ChatRoom;
  } else {
    return {
      id: chatApi.chat,
      participants: transformChatRoomParticipantWebsocketToParticipantRoomItem(chatApi.part),
      events,
      user: {
        avatar: "",
        isPromo: false,
        isB2b: false,
        isCouple: false,
        isUnder18: false,
        status: undefined,
        chatStatus: UserStatus.TRANSFER_PENDING,
        id: "",
        username: i18n.global.t("onboarding.general.personal_area"),
        lastConnection: chatApi.last_event?.created,
        isLoadingUser: false,
        customerUuid: undefined,
      },
      freeSessions: -1,
    } as ChatRoom;
  }
};
